import React from 'react';
import { Link } from 'react-router-dom';

interface SonicHeaderPillProps {
    label: string;
    icon?: React.ReactNode;
    href?: string;
    onClick?: () => void;
    className?: string;
}

/**
 * SonicHeaderPill - Exact match to Sonic UI reference
 * DOM structure matches required specification exactly
 */
const SonicHeaderPill: React.FC<SonicHeaderPillProps> = ({
    label,
    icon,
    href,
    onClick,
    className = '',
}) => {
    const pillContent = (
        <div className="relative group">
            {/* Outer blur glow */}
            <div className="absolute inset-0 bg-gradient-sonic-mirrored animate-gradient rounded-full blur-sm group-hover:blur group-hover:brightness-125 transition opacity-75"></div>

            {/* Main content wrapper */}
            <div className={`relative overflow-hidden z-10 p-[1px] rounded-full bg-gradient-sonic-mirrored animate-gradient shadow-sonic ${className}`}>
                {/* Inner pill body */}
                <div className="flex items-center justify-center gap-x-2 bg-black/85 rounded-full transition px-3 py-1 text-sm group-hover:bg-black/75 backdrop-blur-sm">
                    <span className="text-white/85 font-medium transition group-hover:text-white">
                        <div className="flex flex-row items-center gap-1">
                            {label}
                            {icon}
                        </div>
                    </span>
                </div>

                {/* Shimmer overlay */}
                <div className="pointer-events-none absolute inset-0 z-20 -skew-x-12 overflow-hidden rounded-full">
                    <div className="h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer-sweep" />
                </div>
            </div>
        </div>
    );

    if (href) {
        if (href.startsWith('http')) {
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block hover:-translate-y-0.5 transition-transform duration-200"
                >
                    {pillContent}
                </a>
            );
        }
        return (
            <Link
                to={href}
                className="inline-block hover:-translate-y-0.5 transition-transform duration-200"
            >
                {pillContent}
            </Link>
        );
    }

    return (
        <button
            onClick={onClick}
            className="inline-block hover:-translate-y-0.5 transition-transform duration-200"
        >
            {pillContent}
        </button>
    );
};

export default SonicHeaderPill;

/**
 * SonicHeaderIconPill - Icon-only version with same structure
 */
export const SonicHeaderIconPill: React.FC<{
    icon: React.ReactNode;
    onClick?: () => void;
    to?: string;
    badge?: number;
    badgeColor?: string;
    className?: string;
}> = ({ icon, onClick, to, badge, badgeColor, className = '' }) => {
    const content = (
        <div className="relative group p-0.5">
            {/* Outer blur glow */}
            <div className="absolute inset-0 bg-gradient-sonic-mirrored animate-gradient rounded-full blur-sm group-hover:blur group-hover:brightness-125 transition opacity-75"></div>

            {/* Main content wrapper */}
            <div className={`relative overflow-hidden z-10 p-[1px] rounded-full bg-gradient-sonic-mirrored animate-gradient shadow-sonic ${className}`}>
                {/* Inner icon body */}
                <div className="flex items-center justify-center bg-black/85 rounded-full transition w-9 h-9 group-hover:bg-black/75 backdrop-blur-sm">
                    <span className="text-white/85 transition group-hover:text-white relative flex items-center justify-center">
                        {icon}
                        {badge !== undefined && badge > 0 && (
                            <span className={`absolute -top-2 -right-2 min-w-[16px] h-[16px] ${badgeColor || 'bg-red-500'} text-white text-[9px] rounded-full flex items-center justify-center font-bold px-1 border border-black`}>
                                {badge > 9 ? '9+' : badge}
                            </span>
                        )}
                    </span>
                </div>

                {/* Shimmer overlay */}
                <div className="pointer-events-none absolute inset-0 z-20 -skew-x-12 overflow-hidden rounded-full">
                    <div className="h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer-sweep" />
                </div>
            </div>
        </div>
    );

    if (to) {
        return (
            <Link to={to} onClick={onClick} className="inline-block hover:-translate-y-0.5 transition-transform duration-200">
                {content}
            </Link>
        );
    }

    return (
        <button
            onClick={onClick}
            className="inline-block hover:-translate-y-0.5 transition-transform duration-200"
        >
            {content}
        </button>
    );
};
